import { Box, Button, Select, MenuItem, Typography } from '@mui/material';

const CustomPagination = ({ page, perPage, total, setPage, setPerPage }) => {
  const totalPages = Math.ceil(total / perPage);
  const start = (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePerPageChange = (event) => {
    const newPerPage = Number(event.target.value);
    setPerPage(newPerPage);
    setPage(1);
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const pages = [];
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: '#242424',
        width: '100% !important',
        borderRadius: '0 0 8px 8px',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 2 }
      }}
    >
      {/* Mobile view: Items count and per page dropdown */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          order: 3
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            whiteSpace: 'nowrap',
            color: 'rgba(255, 255, 255, 0.7)'
          }}
        >
          {start}-{end} of {total}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            value={perPage}
            onChange={handlePerPageChange}
            size="small"
            sx={{
              height: '28px',
              fontSize: '12px',
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)'
              },
              '& .MuiSvgIcon-root': {
                color: '#fff'
              }
            }}
          >
            {[10, 20, 50, 100].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              whiteSpace: 'nowrap',
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            Items per page
          </Typography>
        </Box>
      </Box>

      {/* Desktop view: Items count - left */}
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: '12px', sm: '14px' },
          whiteSpace: 'nowrap',
          display: { xs: 'none', sm: 'block' },
          color: 'rgba(255, 255, 255, 0.7)'
        }}
      >
        {start}-{end} of {total}
      </Typography>

      {/* Pagination buttons - center */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'center' },
          gap: { xs: 0.5, sm: 1 },
          width: { xs: '100%', sm: 'auto' },
          order: { xs: 2, sm: 2 }
        }}
      >
        {/* First page */}
        <Button
          onClick={() => handlePageChange(1)}
          disabled={page === 1}
          sx={{
            minWidth: '30px',
            padding: '4px',
            fontSize: '14px',
            color: page === 1 ? 'rgba(255, 255, 255, 0.3)' : '#FFC107',
            '&:hover': {
              backgroundColor: 'rgba(255, 193, 7, 0.08)'
            },
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {'<<'}
        </Button>

        {/* Previous page */}
        <Button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          sx={{
            minWidth: { xs: '32px', sm: '30px' },
            padding: { xs: '4px', sm: '4px' },
            fontSize: { xs: '16px', sm: '14px' },
            color: page === 1 ? 'rgba(255, 255, 255, 0.3)' : '#FFC107',
            '&:hover': {
              backgroundColor: 'rgba(255, 193, 7, 0.08)'
            },
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {'<'}
        </Button>

        {/* Page numbers */}
        {getPageNumbers().map((pageNumber) => (
          <Button
            key={`desktop-${pageNumber}`}
            onClick={() => handlePageChange(pageNumber)}
            sx={{
              minWidth: '30px',
              padding: '4px',
              fontSize: '14px',
              backgroundColor: page === pageNumber ? '#FFC107' : 'transparent',
              color: page === pageNumber ? '#000' : '#fff',
              '&:hover': {
                backgroundColor: page === pageNumber ? '#FFD54F' : 'rgba(255, 193, 7, 0.08)'
              }
            }}
          >
            {pageNumber}
          </Button>
        ))}

        {/* Next page */}
        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          sx={{
            minWidth: { xs: '32px', sm: '30px' },
            padding: { xs: '4px', sm: '4px' },
            fontSize: { xs: '16px', sm: '14px' },
            color: page === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#FFC107',
            '&:hover': {
              backgroundColor: 'rgba(255, 193, 7, 0.08)'
            },
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {'>'}
        </Button>

        {/* Last page */}
        <Button
          onClick={() => handlePageChange(totalPages)}
          disabled={page === totalPages}
          sx={{
            minWidth: '30px',
            padding: '4px',
            fontSize: '14px',
            color: page === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#FFC107',
            '&:hover': {
              backgroundColor: 'rgba(255, 193, 7, 0.08)'
            },
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {'>>'}
        </Button>
      </Box>

      {/* Desktop view: Items per page dropdown - right */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          order: { sm: 3 }
        }}
      >
        <Select
          value={perPage}
          onChange={handlePerPageChange}
          size="small"
          sx={{
            height: '32px',
            fontSize: '14px',
            color: '#fff',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#FFC107'
            },
            '& .MuiSvgIcon-root': {
              color: '#fff'
            }
          }}
        >
          {[10, 20, 50, 100].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            whiteSpace: 'nowrap',
            color: 'rgba(255, 255, 255, 0.7)'
          }}
        >
          Items per page
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomPagination;
